import Auth0ProviderWithNavigate, {
  OauthOptions,
} from '@hellodarwin/app-core/lib/providers/auth0-provider-with-navigate';
import theme from '@hellodarwin/app-core/lib/theme';
import { createReactRootErrorHandlers } from '@hellodarwin/core/lib/components/error/crash-boundary/react-root-error-handlers';
import OnBoardingProvider, {
  OnBoardingStepTypes,
} from '@hellodarwin/core/lib/context/onboarding-provider';
import {
  ClientTranslations,
  MappedTranslations,
} from '@hellodarwin/core/lib/features/entities/translations-entities';
import useApolloClient from '@hellodarwin/core/lib/features/hooks/use-appollo-client';
import { AntdAppProvider } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import AppProvider from '@hellodarwin/core/lib/features/providers/app-data-provider';
import AppTranslationsProvider from '@hellodarwin/core/lib/features/providers/app-translations-provider';
import BreadcrumbsProvider from '@hellodarwin/core/lib/features/providers/breadcrumbs-provider';
import { ModalProvider } from '@hellodarwin/core/lib/features/providers/modal-provider';
import PaginationProvider from '@hellodarwin/core/lib/features/providers/pagination-provider';
import PlatformProvider from '@hellodarwin/core/lib/features/providers/platform-provider';
import AppRouterProvider from '@hellodarwin/core/lib/features/providers/router-provider';
import { ApolloProvider } from '@hellodarwin/core/lib/plugins/apollo';
import { AxiosError } from '@hellodarwin/core/lib/plugins/axios';
import getAntdTheme from '@hellodarwin/core/lib/theme/antd-theme';
import ConfigProvider from 'antd/es/config-provider';
import { createClient } from 'contentful';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import EmailVerificationScreen from './auth0/email-verification-screen';
import { store } from './app/app-store';
import { fetchOauthOptions, resendEmailVerification } from './auth0/use-auth0';
import ClientProfileEditionProvider from './features/providers/client-profile-edition-provider';
import CompanyInfosProviderWrapper from './features/providers/company-infos-provider-wrapper';
import CrashBoundaryWrapper from './features/providers/crash-boundary-wrapper';
import HubspotTrackingProvider from './features/providers/hubspot-tracking-provider';
import LocationProviderWrapper from './features/providers/location-provider-wrapper';
import NavigationGuardProvider from './features/providers/navigation-guard-provider';
import SegmentProvider from './features/providers/segment-provider';
import UserProvider from './features/providers/user-provider';
import { fetchNamespaces } from './i18n';
import { LocalStorageKeys } from './local-storage';
import { unregisterLegacyServiceWorkers } from './unregister-service-worker';

const root = ReactDOM.createRoot(
  document.getElementById('root')!,
  createReactRootErrorHandlers('client'),
);

const DefaultLayout = () => {
  const antdTheme = getAntdTheme(theme);

  const [config, setConfig] = useState<OauthOptions>();
  const [error, setError] = useState<AxiosError>();

  useEffect(() => {
    fetchOauthOptions('client')
      .then((config) => setConfig(config))
      .catch((err) => setError(err));
  }, []);

  const space = process.env.REACT_APP_CONTENTFUL_SPACE_ID || '';
  const accessToken = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '';
  const environment = process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master';
  const space2 = process.env.REACT_APP_CONTENTFUL_SPACE_ID2 || '';
  const accessToken2 = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN2 || '';
  const environment2 =
    process.env.REACT_APP_CONTENTFUL_ENVIRONMENT2 || 'master';
  const contentfulClient = createClient({
    space,
    accessToken,
    environment,
  });
  const contentfulClient2 = createClient({
    space: space2,
    accessToken: accessToken2,
    environment: environment2,
  });

  const client = useApolloClient(space2, accessToken2);

  const jsonFiles: { en: MappedTranslations; fr: MappedTranslations } = {
    en: fetchNamespaces('en'),
    fr: fetchNamespaces('fr'),
  };
  return (
    <PlatformProvider
      theme={theme}
      app="client"
      contentfulClient={contentfulClient}
      contentfulClient2={contentfulClient2}
    >
      <ConfigProvider theme={antdTheme}>
        <AntdAppProvider>
          <AppTranslationsProvider
            translationsFiles={ClientTranslations}
            jsonFiles={jsonFiles}
          >
            <BrowserRouter>
              <NavigationGuardProvider>
                <CrashBoundaryWrapper>
                  <HubspotTrackingProvider />
                  <SegmentProvider writeKey={process.env.REACT_APP_SEGMENT_KEY}>
                    <Provider store={store}>
                      <LocationProviderWrapper>
                        <ApolloProvider client={client}>
                          <Auth0ProviderWithNavigate
                            config={config}
                            EmailVerificationScreenComponent={
                              EmailVerificationScreen
                            }
                            error={error}
                            localStorageKeys={Object.values(LocalStorageKeys)}
                            requireEmailVerification
                            resendEmailVerification={resendEmailVerification}
                          >
                            <CompanyInfosProviderWrapper>
                              <PaginationProvider>
                                <UserProvider>
                                  <AppRouterProvider>
                                    <BreadcrumbsProvider>
                                      <AppProvider>
                                        <OnBoardingProvider
                                          stepType={OnBoardingStepTypes.Client}
                                        >
                                          <ClientProfileEditionProvider>
                                            <ModalProvider>
                                              <App />
                                            </ModalProvider>
                                          </ClientProfileEditionProvider>
                                        </OnBoardingProvider>
                                      </AppProvider>
                                    </BreadcrumbsProvider>
                                  </AppRouterProvider>
                                </UserProvider>
                              </PaginationProvider>
                            </CompanyInfosProviderWrapper>
                          </Auth0ProviderWithNavigate>
                        </ApolloProvider>
                      </LocationProviderWrapper>{' '}
                    </Provider>
                  </SegmentProvider>
                </CrashBoundaryWrapper>
              </NavigationGuardProvider>
            </BrowserRouter>
          </AppTranslationsProvider>
        </AntdAppProvider>
      </ConfigProvider>
    </PlatformProvider>
  );
};

root.render(
  <React.StrictMode>
    <DefaultLayout />
  </React.StrictMode>,
);

void unregisterLegacyServiceWorkers();
